import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PuzzleService, formatPuzzleDisplayTitle, canonicalPuzzleTitle, type Puzzle, type SolutionMove } from '../../services/puzzleService';
import { GameBoard } from '../game/GameBoard';
import type { Cell, GameState } from '../../game/types';
import { copyBoard, isValidMove, makeMove, hasLegalMoves, wouldCreateLineTooLong } from '../../game/logic/board';
import { checkForYugos, processYugos } from '../../game/logic/yugos';
import { checkForIgo } from '../../game/logic/igo';
import { countYugos } from '../../game/logic/scoring';
import { playSound } from '../../utils/sound';
import { loadSavedSettings } from '../../utils/settings';
import { trackPuzzleStart, trackPuzzleSolve } from '../../utils/visitorAnalytics';
import './PuzzleView.css';

interface PuzzleViewProps {
  puzzleId: number;
  puzzleIds?: number[];
  onGoToPuzzle?: (puzzleId: number) => void;
  onBack: () => void;
}

const squareNotation = (row: number, col: number) =>
  `${String.fromCharCode(97 + col)}${8 - row}`;

const normalizePuzzleBlurb = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const isRedundantPuzzleDescription = (puzzle: Puzzle) => {
  const description = normalizePuzzleBlurb(puzzle.description || '');
  if (!description) return true;

  const title = normalizePuzzleBlurb(puzzle.title || '');
  const displayTitle = normalizePuzzleBlurb(formatPuzzleDisplayTitle(puzzle));
  const identity = normalizePuzzleBlurb(
    `${canonicalPuzzleTitle(puzzle)} ${puzzle.difficulty} ${puzzle.creator_username || ''}`
  );

  return (
    description === title ||
    description === displayTitle ||
    description === identity ||
    (/to move/.test(description) && /igo in \d+/.test(description))
  );
};

const PuzzleView: React.FC<PuzzleViewProps> = ({ puzzleId, puzzleIds = [], onGoToPuzzle, onBack }) => {
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [catalogIds, setCatalogIds] = useState<number[]>(puzzleIds);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [moveHistory, setMoveHistory] = useState<Array<{ row: number; col: number; player: 'white' | 'black' }>>([]);
  const [isSolved, setIsSolved] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [incorrectMoveCell, setIncorrectMoveCell] = useState<{row: number; col: number} | null>(null);
  const [incorrectMoveMessage, setIncorrectMoveMessage] = useState<string | null>(null);
  const [newlyPlacedDots, setNewlyPlacedDots] = useState<Set<string>>(new Set());
  const [fadingDots, setFadingDots] = useState<Set<string>>(new Set());
  const [isReviewingSolution, setIsReviewingSolution] = useState(false);
  const [solutionStepIndex, setSolutionStepIndex] = useState(-1);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [currentTheme, setCurrentTheme] = useState('classic');
  const loadGenerationRef = useRef(0);

  // Load sound settings and theme
  useEffect(() => {
    const settings = loadSavedSettings();
    setSoundEnabled(settings.soundEnabled);
    
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('migoyugoTheme');
    if (savedTheme && ['classic', 'high-contrast', 'galaxy', 'neon', 'marble', 'wood', 'custom'].includes(savedTheme)) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  useEffect(() => {
    if (puzzleIds.length > 0) {
      setCatalogIds(puzzleIds);
    }
  }, [puzzleIds]);

  useEffect(() => {
    if (puzzleIds.length > 0) return;
    let cancelled = false;
    PuzzleService.getPuzzles({
      limit: 500,
      offset: 0,
      orderBy: 'catalog_number',
      orderDir: 'asc',
    }).then((result) => {
      if (cancelled || !result.success || !result.data) return;
      setCatalogIds(result.data.puzzles.map((item) => item.id));
    }).catch(() => {
      /* browse list is optional for prev/next */
    });
    return () => {
      cancelled = true;
    };
  }, [puzzleIds.length]);

  useEffect(() => {
    const loadPuzzle = async () => {
      const generation = ++loadGenerationRef.current;
      setLoading(true);
      setError(null);
      setMoveHistory([]);
      setIsSolved(false);
      setIsFailed(false);
      setIncorrectMoveCell(null);
      setIncorrectMoveMessage(null);
      setNewlyPlacedDots(new Set());
      setFadingDots(new Set());
      setIsReviewingSolution(false);
      setSolutionStepIndex(-1);
      setIsAutoPlaying(false);
      
      try {
        const result = await PuzzleService.getPuzzleById(puzzleId);
        if (generation !== loadGenerationRef.current) return;
        
        if (result.success && result.puzzle) {
          setPuzzle(result.puzzle);
          trackPuzzleStart(result.puzzle.id);
          
          // Initialize game state from puzzle
          const initialBoard = copyBoard(result.puzzle.initial_board);
          const scores = {
            white: countYugos(initialBoard, 'white'),
            black: countYugos(initialBoard, 'black')
          };
          
          setGameState({
            board: initialBoard,
            currentPlayer: result.puzzle.starting_player,
            scores,
            gameStatus: 'active',
            lastMove: null,
            players: { white: 'You', black: 'Puzzle' }
          });
        } else {
          setError(result.error || 'Failed to load puzzle');
        }
      } catch (err) {
        if (generation !== loadGenerationRef.current) return;
        setError('An error occurred while loading the puzzle');
        console.error('Error loading puzzle:', err);
      } finally {
        if (generation === loadGenerationRef.current) {
          setLoading(false);
        }
      }
    };

    loadPuzzle();
  }, [puzzleId]);

  // Helper to check if current position in solution is a player move
  // Player only plays moves of the starting_player color
  // All opponent moves are auto-played by the system
  const isPlayerMove = useCallback((moveIndex: number) => {
    if (!puzzle || !puzzle.solution_moves) return false;
    if (moveIndex >= puzzle.solution_moves.length) return false;
    
    // Player only plays moves of the starting_player color
    // If starting_player is 'white', player plays white moves, system plays black moves
    // If starting_player is 'black', player plays black moves, system plays white moves
    return puzzle.solution_moves[moveIndex].player === puzzle.starting_player;
  }, [puzzle]);

  // Helper to get the expected move for current position
  const getExpectedMove = useCallback(() => {
    if (!puzzle || !puzzle.solution_moves) return null;
    if (moveHistory.length >= puzzle.solution_moves.length) return null;
    return puzzle.solution_moves[moveHistory.length];
  }, [puzzle, moveHistory]);

  // Auto-play a move (for system moves)
  const autoPlayMove = useCallback(async (move: { row: number; col: number; player: 'white' | 'black' }, delay: number = 500) => {
    const generation = loadGenerationRef.current;
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        if (generation !== loadGenerationRef.current) {
          resolve();
          return;
        }
        // Use functional update to get latest state
        setGameState(prev => {
          if (generation !== loadGenerationRef.current) {
            resolve();
            return prev;
          }
          if (!prev) {
            resolve();
            return null;
          }

          // makeMove returns the updated board with piece placed and Yugos processed
          const newBoard = makeMove(copyBoard(prev.board), move.row, move.col, move.player);
          
          // Check for removed cells for animation (compare before/after)
          const beforeBoard = prev.board;
          const removedCells: {row: number, col: number}[] = [];
          
          for (let r = 0; r < 8; r++) {
            for (let c = 0; c < 8; c++) {
              if (beforeBoard[r][c] && !newBoard[r][c]) {
                removedCells.push({ row: r, col: c });
              }
            }
          }
          
          // Trigger fade-out animation for removed dots
          if (removedCells.length > 0) {
            const fadeSet = new Set<string>();
            removedCells.forEach(({ row: r, col: c }) => {
              fadeSet.add(`${r}-${c}`);
            });
            setFadingDots(fadeSet);
            setTimeout(() => {
              setFadingDots(new Set());
            }, 500);
          }
          
          // Check for Igo
          let igoLine: {row: number, col: number}[] | null = null;
          const placedCell = newBoard[move.row][move.col];
          if (placedCell && placedCell.isYugo && placedCell.color === move.player) {
            const igo = checkForIgo(newBoard, move.row, move.col, move.player);
            if (igo) {
              igoLine = igo;
            }
          }
          
          const scores = {
            white: countYugos(newBoard, 'white'),
            black: countYugos(newBoard, 'black')
          };
          
          // Play sound effects based on move result
          if (igoLine) {
            playSound('igo', soundEnabled);
          } else if (placedCell && placedCell.isYugo) {
            playSound('yugo', soundEnabled);
          } else {
            playSound('migo', soundEnabled);
          }
          
          // Check for Wego (no legal moves)
          if (!igoLine) {
            const nextPlayer = move.player === 'white' ? 'black' : 'white';
            if (!hasLegalMoves(newBoard, nextPlayer)) {
              playSound('wego', soundEnabled);
            }
          }
          
          setMoveHistory(prev => [...prev, move]);
          
          // Animation for newly placed dot
          const cellKey = `${move.row}-${move.col}`;
          setNewlyPlacedDots(new Set([cellKey]));
          setTimeout(() => {
            setNewlyPlacedDots(new Set());
          }, 300);
          
          resolve();
          
          // Return updated state
          return {
            ...prev,
            board: newBoard,
            currentPlayer: move.player === 'white' ? 'black' : 'white',
            scores,
            gameStatus: igoLine ? 'finished' : 'active',
            lastMove: move,
            igoLine: igoLine || undefined
          };
        });
      }, delay);
    });
  }, [soundEnabled]);

  const applySolutionIndex = useCallback((index: number) => {
    if (!puzzle || !puzzle.solution_moves) return;

    const moves = puzzle.solution_moves;
    const clamped = Math.max(-1, Math.min(index, moves.length - 1));
    let board = copyBoard(puzzle.initial_board);
    let lastMove: { row: number; col: number; player: 'white' | 'black' } | null = null;
    let igoLine: { row: number; col: number }[] | null = null;
    let currentPlayer: 'white' | 'black' = puzzle.starting_player;

    for (let i = 0; i <= clamped; i++) {
      const move = moves[i];
      board = makeMove(board, move.row, move.col, move.player);
      lastMove = { row: move.row, col: move.col, player: move.player };
      igoLine = null;
      const placedCell = board[move.row][move.col];
      if (placedCell && placedCell.isYugo && placedCell.color === move.player) {
        const igo = checkForIgo(board, move.row, move.col, move.player);
        if (igo) igoLine = igo;
      }
      currentPlayer = move.player === 'white' ? 'black' : 'white';
    }

    if (clamped < 0) {
      currentPlayer = puzzle.starting_player;
      lastMove = null;
      igoLine = null;
    }

    setSolutionStepIndex(clamped);
    setMoveHistory(clamped < 0 ? [] : moves.slice(0, clamped + 1).map((move) => ({
      row: move.row,
      col: move.col,
      player: move.player
    })));
    setGameState({
      board,
      currentPlayer,
      scores: {
        white: countYugos(board, 'white'),
        black: countYugos(board, 'black')
      },
      gameStatus: igoLine ? 'finished' : 'active',
      lastMove,
      players: { white: 'You', black: 'Puzzle' },
      igoLine: igoLine || undefined
    });

    if (clamped >= 0) {
      const move = moves[clamped];
      setNewlyPlacedDots(new Set([`${move.row}-${move.col}`]));
      setTimeout(() => setNewlyPlacedDots(new Set()), 300);
    } else {
      setNewlyPlacedDots(new Set());
    }
  }, [puzzle]);

  const enterSolutionReview = useCallback((index: number) => {
    setIsReviewingSolution(true);
    setIsAutoPlaying(false);
    setIsFailed(false);
    setIncorrectMoveCell(null);
    setIncorrectMoveMessage(null);
    applySolutionIndex(index);
  }, [applySolutionIndex]);

  const handleCellClick = useCallback(async (row: number, col: number) => {
    if (!gameState || gameState.gameStatus !== 'active' || isSolved || isFailed || isAutoPlaying || isReviewingSolution) return;
    const generation = loadGenerationRef.current;
    
    // Check if move would create a line longer than 4
    if (wouldCreateLineTooLong(gameState.board, row, col, gameState.currentPlayer)) {
      // Show error for long line
      setIncorrectMoveCell({ row, col });
      setIncorrectMoveMessage('Incorrect move - long line');
      setIsFailed(true);
      
      // Play illegal move sound
      if (soundEnabled) {
        try {
          const audio = new Audio('/sounds/illegal-move.mp3');
          audio.volume = 0.5;
          audio.play().catch(() => {
            // Sound play failed - silently fail
          });
        } catch (e) {
          // Sound loading failed - silently fail
        }
      }
      
      // Clear error after 1 second
      setTimeout(() => {
        setIncorrectMoveCell(null);
        setIncorrectMoveMessage(null);
        setIsFailed(false);
      }, 1000);
      return;
    }
    
    // Check if move is valid
    if (!isValidMove(gameState.board, row, col, gameState.currentPlayer)) {
      return;
    }

    // Check if this is a player move (not a system move)
    const expectedMove = getExpectedMove();
    if (!expectedMove) {
      // No more moves expected
      return;
    }

    // Check if the move matches the expected solution move. On the final move an
    // Igo can sometimes be completed at either end of an open line of yugos, so
    // any square recorded as an alternate is equally correct.
    const isFinalSolutionMove =
      !!puzzle?.solution_moves && moveHistory.length === puzzle.solution_moves.length - 1;
    const matchesAlternate =
      isFinalSolutionMove &&
      (expectedMove.alternates || []).some((alt) => alt.row === row && alt.col === col);

    const isCorrect = (
      gameState.currentPlayer === expectedMove.player &&
      ((row === expectedMove.row && col === expectedMove.col) || matchesAlternate)
    );

    if (!isCorrect) {
      // Incorrect move - show error
      setIncorrectMoveCell({ row, col });
      setIncorrectMoveMessage('Incorrect move. Try again.');
      setIsFailed(true);
      
      // Play illegal move sound
      if (soundEnabled) {
        try {
          const audio = new Audio('/sounds/illegal-move.mp3');
          audio.volume = 0.5;
          audio.play().catch(() => {
            // Sound play failed - silently fail
          });
        } catch (e) {
          // Sound loading failed - silently fail
        }
      }
      
      // Clear error after 1 second
      setTimeout(() => {
        setIncorrectMoveCell(null);
        setIncorrectMoveMessage(null);
        setIsFailed(false);
      }, 1000);
      return;
    }

    // Correct move - proceed with making the move
    setIsFailed(false);
    setIncorrectMoveCell(null);
    setIncorrectMoveMessage(null);

    // Make the player's move - makeMove returns the updated board with piece placed and Yugos processed
    const beforeBoard = copyBoard(gameState.board);
    let currentBoard = makeMove(beforeBoard, row, col, gameState.currentPlayer);
    
    // Check for removed cells for animation (compare before/after)
    const removedCells: {row: number, col: number}[] = [];
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        if (beforeBoard[r][c] && !currentBoard[r][c]) {
          removedCells.push({ row: r, col: c });
        }
      }
    }
    
    // Trigger fade-out animation for removed dots
    if (removedCells.length > 0) {
      const fadeSet = new Set<string>();
      removedCells.forEach(({ row: r, col: c }) => {
        fadeSet.add(`${r}-${c}`);
      });
      setFadingDots(fadeSet);
      setTimeout(() => {
        setFadingDots(new Set());
      }, 500);
    }
    
    // Check for Igo (4 Yugos in a line)
    let igoLine: {row: number, col: number}[] | null = null;
    const placedCell = currentBoard[row][col];
    if (placedCell && placedCell.isYugo && placedCell.color === gameState.currentPlayer) {
      const igo = checkForIgo(currentBoard, row, col, gameState.currentPlayer);
      if (igo) {
        igoLine = igo;
      }
    }
    
    const scores = {
      white: countYugos(currentBoard, 'white'),
      black: countYugos(currentBoard, 'black')
    };
    
    // Play sound effects based on move result
    if (igoLine) {
      playSound('igo', soundEnabled);
    } else if (placedCell && placedCell.isYugo) {
      playSound('yugo', soundEnabled);
    } else {
      playSound('migo', soundEnabled);
    }
    
    // Check for Wego (no legal moves)
    if (!igoLine) {
      const nextPlayer = gameState.currentPlayer === 'white' ? 'black' : 'white';
      if (!hasLegalMoves(currentBoard, nextPlayer)) {
        playSound('wego', soundEnabled);
      }
    }
    
    // Add player move to history
    const playerMove = { row, col, player: gameState.currentPlayer };
    const updatedHistory = [...moveHistory, playerMove];
    setMoveHistory(updatedHistory);
    
    // Update game state after player move using functional update
    const currentPlayer: 'white' | 'black' = gameState.currentPlayer === 'white' ? 'black' : 'white';
    setGameState(prev => prev ? {
      ...prev,
      board: currentBoard,
      currentPlayer,
      scores,
      gameStatus: igoLine ? 'finished' : 'active',
      lastMove: playerMove,
      igoLine: igoLine || undefined
    } : null);
    
    // Animation for newly placed dot
    const cellKey = `${row}-${col}`;
    setNewlyPlacedDots(new Set([cellKey]));
    setTimeout(() => {
      setNewlyPlacedDots(new Set());
    }, 300);

    // Wait a bit to ensure the board renders the move before auto-playing
    await new Promise(resolve => setTimeout(resolve, 400));
    if (generation !== loadGenerationRef.current) return;

    // Check if puzzle is solved (all moves completed)
    if (!puzzle || !puzzle.solution_moves || updatedHistory.length >= puzzle.solution_moves.length) {
      setIsSolved(true);
      if (puzzle) trackPuzzleSolve(puzzle.id);
      if (puzzle?.solution_moves?.length) {
        enterSolutionReview(puzzle.solution_moves.length - 1);
      }
      return;
    }

    // Auto-play system moves until next player turn
    if (!puzzle || !puzzle.solution_moves) {
      setIsAutoPlaying(false);
      return;
    }

    setIsAutoPlaying(true);
    let nextMoveIndex = updatedHistory.length;

    while (nextMoveIndex < puzzle.solution_moves.length) {
      const nextMove = puzzle.solution_moves[nextMoveIndex];
      
      // Check if this is a player move (stop auto-playing)
      const isNextMovePlayerMove = isPlayerMove(nextMoveIndex);
      
      if (isNextMovePlayerMove) {
        // Next move is a player move, stop auto-playing
        break;
      }

      // This is a system move, auto-play it
      await autoPlayMove(nextMove, 600);
      if (generation !== loadGenerationRef.current) return;
      
      // Wait for state to update before next iteration
      await new Promise(resolve => setTimeout(resolve, 200));
      if (generation !== loadGenerationRef.current) return;
      nextMoveIndex++;
    }

    setIsAutoPlaying(false);

    // Check if puzzle is now solved
    const finalHistory = [...updatedHistory];
    for (let i = updatedHistory.length; i < nextMoveIndex; i++) {
      if (puzzle.solution_moves && puzzle.solution_moves[i]) {
        finalHistory.push(puzzle.solution_moves[i]);
      }
    }
    
    if (puzzle.solution_moves && finalHistory.length >= puzzle.solution_moves.length) {
      setIsSolved(true);
      trackPuzzleSolve(puzzle.id);
      enterSolutionReview(puzzle.solution_moves.length - 1);
    }
  }, [gameState, moveHistory, isSolved, isFailed, puzzle, isAutoPlaying, isReviewingSolution, getExpectedMove, autoPlayMove, enterSolutionReview]);

  const handleReset = () => {
    if (!puzzle) return;
    
    const initialBoard = copyBoard(puzzle.initial_board);
    const scores = {
      white: countYugos(initialBoard, 'white'),
      black: countYugos(initialBoard, 'black')
    };
    
    setGameState({
      board: initialBoard,
      currentPlayer: puzzle.starting_player,
      scores,
      gameStatus: 'active',
      lastMove: null,
      players: { white: 'You', black: 'Puzzle' }
    });
    
    setMoveHistory([]);
    setIsSolved(false);
    setIsFailed(false);
    setIncorrectMoveCell(null);
    setIncorrectMoveMessage(null);
    setIsReviewingSolution(false);
    setSolutionStepIndex(-1);
    setIsAutoPlaying(false);
  };

  const handleShowSolution = useCallback(() => {
    if (!puzzle || !puzzle.solution_moves) return;
    enterSolutionReview(-1);
  }, [puzzle, enterSolutionReview]);

  if (loading && !puzzle) {
    return (
      <div className="puzzle-view">
        <div className="puzzle-view-loading">
          <p>Loading puzzle...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="puzzle-view">
        <div className="puzzle-view-error">
          <p>Error: {error}</p>
          <button onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!puzzle || !gameState) {
    return null;
  }

  const currentPuzzleIndex = catalogIds.indexOf(puzzleId);
  const prevPuzzleId = currentPuzzleIndex > 0 ? catalogIds[currentPuzzleIndex - 1] : null;
  const nextPuzzleId =
    currentPuzzleIndex >= 0 && currentPuzzleIndex < catalogIds.length - 1
      ? catalogIds[currentPuzzleIndex + 1]
      : null;

  return (
    <div className="puzzle-view">
      <div className="puzzle-view-content">
        <div className="puzzle-view-info-panel">
          <div className="puzzle-view-info-section">
            <h3>{formatPuzzleDisplayTitle(puzzle)}</h3>
            {puzzle.description && !isRedundantPuzzleDescription(puzzle) && (
              <p className="puzzle-view-description">{puzzle.description}</p>
            )}
            <div className="puzzle-view-meta">
              <span className={`puzzle-view-difficulty difficulty-${puzzle.difficulty}`}>
                {puzzle.difficulty}
              </span>
              <span className="puzzle-view-creator">By {puzzle.creator_username}</span>
            </div>
          </div>
          <div className="puzzle-view-controls">
            <button 
              onClick={handleReset} 
              className="puzzle-view-reset-btn"
              disabled={isAutoPlaying}
            >
              Reset Puzzle
            </button>
            {!isReviewingSolution && puzzle.solution_moves && puzzle.solution_moves.length > 0 && (
              <button
                onClick={handleShowSolution}
                className="puzzle-view-show-solution-btn"
                disabled={isAutoPlaying || isSolved}
              >
                Show Solution
              </button>
            )}
          </div>
          {isReviewingSolution && puzzle.solution_moves && puzzle.solution_moves.length > 0 && (
            <div className="puzzle-view-review">
              <div className="puzzle-view-review-banner">
                {isSolved ? 'Puzzle solved — review the line' : 'Solution'}
              </div>
              <div className="puzzle-view-review-nav">
                <button
                  type="button"
                  className="puzzle-view-review-btn"
                  onClick={() => applySolutionIndex(solutionStepIndex - 1)}
                  disabled={solutionStepIndex < 0}
                >
                  ← Prev
                </button>
                <span className="puzzle-view-review-counter">
                  {solutionStepIndex < 0
                    ? 'Start'
                    : `${solutionStepIndex + 1} / ${puzzle.solution_moves.length}`}
                </span>
                <button
                  type="button"
                  className="puzzle-view-review-btn"
                  onClick={() => applySolutionIndex(solutionStepIndex + 1)}
                  disabled={solutionStepIndex >= puzzle.solution_moves.length - 1}
                >
                  Next →
                </button>
              </div>
              <div className="puzzle-view-move-log">
                {Array.from(
                  {
                    length:
                      puzzle.starting_player === 'black'
                        ? Math.ceil((puzzle.solution_moves.length + 1) / 2)
                        : Math.ceil(puzzle.solution_moves.length / 2)
                  },
                  (_, pairIndex) => {
                    const moves = puzzle.solution_moves!;
                    let whiteMove: SolutionMove | undefined;
                    let blackMove: SolutionMove | undefined;
                    let whiteIndex = -1;
                    let blackIndex = -1;

                    if (puzzle.starting_player === 'black' && pairIndex === 0) {
                      blackMove = moves[0];
                      blackIndex = 0;
                    } else if (puzzle.starting_player === 'black') {
                      whiteMove = moves[pairIndex * 2 - 1];
                      blackMove = moves[pairIndex * 2];
                      whiteIndex = pairIndex * 2 - 1;
                      blackIndex = pairIndex * 2;
                    } else {
                      whiteMove = moves[pairIndex * 2];
                      blackMove = moves[pairIndex * 2 + 1];
                      whiteIndex = pairIndex * 2;
                      blackIndex = pairIndex * 2 + 1;
                    }

                    const renderSquare = (
                      move: SolutionMove | undefined,
                      index: number
                    ) => {
                      if (!move) {
                        return <span className="puzzle-view-move-sq empty">--</span>;
                      }
                      const isActive = solutionStepIndex === index;
                      const isPlayer = move.player === puzzle.starting_player;
                      return (
                        <button
                          type="button"
                          className={`puzzle-view-move-sq${isActive ? ' active' : ''}${isPlayer ? ' player-move' : ''}`}
                          onClick={() => applySolutionIndex(index)}
                        >
                          {squareNotation(move.row, move.col)}
                        </button>
                      );
                    };

                    return (
                      <div key={pairIndex} className="puzzle-view-move-entry">
                        <span className="puzzle-view-move-number">{pairIndex + 1}.</span>
                        {renderSquare(whiteMove, whiteIndex)}
                        {renderSquare(blackMove, blackIndex)}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="puzzle-view-board-container">
          {catalogIds.length > 1 && onGoToPuzzle && (
            <div className="puzzle-view-puzzle-nav">
              <button
                type="button"
                className="puzzle-view-puzzle-nav-btn"
                onClick={() => prevPuzzleId != null && onGoToPuzzle(prevPuzzleId)}
                disabled={prevPuzzleId == null}
                aria-label="Previous puzzle"
              >
                ← Puzzle
              </button>
              <span className="puzzle-view-puzzle-nav-label">
                {currentPuzzleIndex >= 0
                  ? `${currentPuzzleIndex + 1} / ${catalogIds.length}`
                  : 'Puzzle'}
              </span>
              <button
                type="button"
                className="puzzle-view-puzzle-nav-btn"
                onClick={() => nextPuzzleId != null && onGoToPuzzle(nextPuzzleId)}
                disabled={nextPuzzleId == null}
                aria-label="Next puzzle"
              >
                Puzzle →
              </button>
            </div>
          )}
          {isSolved && !isReviewingSolution && (
            <div className="puzzle-view-success">
              <h3>🎉 Puzzle Solved!</h3>
              <p>Congratulations! You found the correct solution.</p>
            </div>
          )}
          
          {incorrectMoveMessage && (
            <div className="puzzle-view-failure">
              <p>{incorrectMoveMessage}</p>
            </div>
          )}
          
          <GameBoard
            gameState={gameState}
            isReviewMode={isReviewingSolution}
            newlyPlacedDots={newlyPlacedDots}
            fadingDots={fadingDots}
            onCellClick={handleCellClick}
            incorrectCell={incorrectMoveCell}
            currentTheme={currentTheme}
          />
        </div>
      </div>
    </div>
  );
};

export default PuzzleView;


